// SPDX-License-Identifier: LicenseRef-Syncanix-Proprietary
import { CircleCheck, CircleSlash, OctagonAlert, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Medallion, Panel } from '@/components/bedrock';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { orderIncidents } from './incident-order';
import { IncidentCard } from './IncidentCard';
import { DOWN_TONE, markStyle, pillStyle, RESOLVED_TONE, SEVERITY_TONE } from './status-tone';

import type { MedallionHue } from '@/components/bedrock';
import type { Translator } from '@/i18n/translator';
import type { ReactElement } from 'react';

/**
 * Public system-status page (D30) at `/status`.
 *
 * Reads `/status/data.json` — the StatusStack writer's output, served
 * same-origin through the marketing Amplify proxy rewrite — so this page
 * lives in a DIFFERENT failure domain than the api Lambda / Aurora (the
 * point of a status page). The prerendered shell shows the loading state;
 * live data hydrates client-side. A full account/region outage can still
 * take this page down — that residual fate-sharing is the accepted trade
 * of the self-hosted decision (PLAN D30 operator answer).
 */

export interface StatusSurface {
  readonly id: string;
  readonly label: string;
  readonly ok: boolean;
  readonly httpStatus: number;
  readonly latencyMs: number;
}

export interface StatusIncident {
  readonly id: string;
  readonly severity: 'maintenance' | 'degraded' | 'outage';
  readonly startedAt: string;
  readonly resolvedAt?: string;
  readonly title: string | Readonly<Record<string, string>>;
  readonly body?: string | Readonly<Record<string, string>>;
}

export interface StatusDocument {
  readonly generatedAt: string;
  readonly environment: string;
  readonly overall: 'operational' | 'degraded' | 'outage';
  readonly surfaces: readonly StatusSurface[];
  readonly incidents: readonly StatusIncident[];
}

const STALE_AFTER_MS = 15 * 60_000;

/** Surface labels localize by id; unknown ids fall back to the writer's label. */
function surfaceLabel(t: Translator, surface: StatusSurface): string {
  const key = `status.surface.${surface.id}`;
  const translated = t(key);
  return translated === key ? surface.label : translated;
}

type FeedState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; doc: StatusDocument };

export function Status(): ReactElement {
  const { t, locale } = useT();
  useLocalizedSeoMeta({
    title: t('status.title'),
    description: t('status.seoDescription'),
    path: '/status',
  });

  const [feed, setFeed] = useState<FeedState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetch('/status/data.json', { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`status feed ${String(res.status)}`);
        return res.json() as Promise<StatusDocument>;
      })
      .then((doc) => {
        setFeed({ kind: 'ready', doc });
      })
      .catch(() => {
        if (!controller.signal.aborted) setFeed({ kind: 'error' });
      });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      <PageHero
        width="prose"
        align="start"
        title={t('status.heading')}
        description={t('status.intro')}
      />
      <Container width="prose" className="pb-16 pt-4 sm:pb-20">
        {feed.kind === 'loading' && (
          <FeedPlaceholder tone="loading" message={t('status.loading')} />
        )}
        {feed.kind === 'error' && <FeedPlaceholder tone="error" message={t('status.feedError')} />}
        {feed.kind === 'ready' && <StatusBody doc={feed.doc} t={t} locale={locale} />}

        <footer className="mt-16 border-t border-[color:var(--bd-line)] pt-6">
          <p className="text-sm text-[color:var(--bd-muted)]">{t('status.footerNote')}</p>
        </footer>
      </Container>
    </main>
  );
}

/**
 * The two non-ready states, as a designed surface rather than a stray sentence.
 *
 * Both used to render one grey paragraph under a masthead, leaving ~450px of
 * empty page above the footer. On a status page that is the worst possible
 * shape: a visitor arrives here precisely when they suspect something is
 * broken, and a page that looks unfinished is indistinguishable from one that
 * IS. A bordered panel with a mark says "this page is working and here is what
 * it knows", which is the message even the failure case needs to carry.
 *
 * `role="status"` is kept on the message itself so the live region announces
 * the text and not the decoration.
 */
function FeedPlaceholder({
  tone,
  message,
}: {
  tone: 'loading' | 'error';
  message: string;
}): ReactElement {
  return (
    <Panel className="mt-10 flex items-start gap-4 p-6">
      {/* The hue follows the tone for the same reason the GLYPH does. It was
          pinned to `blue` while the glyph switched to a warning triangle, so
          the error state asserted two different things at once: the shape said
          warning and the colour said information. `warn` exists on
          `MedallionHue` precisely so a status surface can say the one thing —
          and this was the only `<Medallion>` on the site with a hardcoded hue
          rather than one read from a tone table. */}
      <Medallion
        icon={tone === 'error' ? TriangleAlert : CircleSlash}
        hue={tone === 'error' ? 'warn' : 'blue'}
      />
      <div className="min-w-0">
        <p className="text-[0.95rem] leading-[1.6] text-[color:var(--bd-body)]" role="status">
          {message}
        </p>
        {/* Three inert rows in the shape of the surfaces list. They say what the
            panel is waiting for, so the wait reads as a pending answer rather
            than as an empty page. */}
        <ul aria-hidden="true" className="mt-5 grid gap-2">
          {[0, 1, 2].map((row) => (
            <li
              key={row}
              className="h-3 rounded-full bg-[color:var(--bd-line)]"
              style={{ width: `${String(78 - row * 14)}%` }}
            />
          ))}
        </ul>
      </div>
    </Panel>
  );
}

/**
 * The verdict mark, per overall state.
 *
 * `degraded` and `outage` both drew a violet triangle, so the page announced a
 * service disruption in the brand's identity purple and gave the two states the
 * same glyph. They are different answers to the question the visitor came with,
 * and they now look different: a warning triangle in amber, a stop-sign in red.
 */
const OVERALL_MARK: Record<
  StatusDocument['overall'],
  { readonly icon: typeof CircleCheck; readonly hue: MedallionHue }
> = {
  operational: { icon: CircleCheck, hue: 'ok' },
  degraded: { icon: TriangleAlert, hue: 'warn' },
  outage: { icon: OctagonAlert, hue: 'danger' },
};

const OVERALL_TONE: Record<StatusDocument['overall'], Parameters<typeof pillStyle>[0]> = {
  operational: RESOLVED_TONE,
  degraded: SEVERITY_TONE.degraded,
  outage: SEVERITY_TONE.outage,
};

function StatusBody({
  doc,
  t,
  locale,
}: {
  doc: StatusDocument;
  t: Translator;
  locale: string;
}): ReactElement {
  const generated = new Date(doc.generatedAt);
  const stale = Date.now() - generated.getTime() > STALE_AFTER_MS;
  const timeFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="mt-10 flex flex-col gap-10">
      {/* The verdict is the reason anyone opens this page, so it is a panel with
          an accent rule rather than a small pill above a caption. */}
      <Panel accent aria-label={t('status.overallLabel')} as="section" className="p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Medallion icon={OVERALL_MARK[doc.overall].icon} hue={OVERALL_MARK[doc.overall].hue} />
          <span
            className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium"
            style={pillStyle(OVERALL_TONE[doc.overall])}
          >
            {t(`status.overall.${doc.overall}`)}
          </span>
          <p className="text-sm text-[color:var(--bd-muted)]">
            {t('status.lastUpdated', { time: timeFormat.format(generated) })}
          </p>
        </div>
        {stale && (
          <p className="mt-3 text-sm font-medium text-warning-foreground" role="alert">
            {t('status.stale')}
          </p>
        )}
      </Panel>

      <section aria-label={t('status.surfacesLabel')}>
        <h2 className="text-lg font-semibold text-[color:var(--bd-ink)]">
          {t('status.surfacesLabel')}
        </h2>
        {/* A down component is the answer the visitor came for, so it is the
            loudest row rather than the same hairline row with a red word: a
            dot, a tinted ground, and the label carried in the ink of the state
            it is in. */}
        <ul className="mt-4 flex flex-col divide-y divide-hairline overflow-hidden rounded-lg border border-[color:var(--bd-line)]">
          {doc.surfaces.map((surface) => {
            const tone = surface.ok ? RESOLVED_TONE : DOWN_TONE;
            return (
              <li
                key={surface.id}
                className="flex items-center justify-between gap-4 px-4 py-3"
                style={
                  surface.ok
                    ? undefined
                    : { background: `color-mix(in srgb, ${tone.fill} 7%, var(--bd-page))` }
                }
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="size-2 flex-none rounded-full"
                    style={markStyle(tone)}
                  />
                  <span className="truncate text-sm text-[color:var(--bd-ink)]">
                    {surfaceLabel(t, surface)}
                  </span>
                </span>
                <span className="flex-none text-sm font-medium" style={{ color: tone.ink }}>
                  {surface.ok ? t('status.surfaceUp') : t('status.surfaceDown')}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-label={t('status.incidentsHeading')}>
        <h2 className="text-lg font-semibold text-[color:var(--bd-ink)]">
          {t('status.incidentsHeading')}
        </h2>
        {doc.incidents.length === 0 ? (
          <p className="mt-4 text-sm text-[color:var(--bd-muted)]">{t('status.noIncidents')}</p>
        ) : (
          <ul className="mt-4 flex flex-col gap-4">
            {orderIncidents(doc.incidents).map((incident) => (
              <IncidentCard key={incident.id} incident={incident} t={t} locale={locale} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
